import { Injectable } from '@angular/core';
import { LocaleTokenSearch } from '../../state/models/locale-token';
import { LocaleTokenValueEntityService } from '../../state/entity-services/locale-token-value-entity.service';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class LocaleValuesService {

  constructor(
    private sanitizer: DomSanitizer,
    private localeValuesEntityService: LocaleTokenValueEntityService,
  ) {
  }

  /**
   * Returns the localized string with placeholders (e.g. `{{key}}`) replaced by corresponding values.
   * Can return either raw string or HTML-safe content depending on the `asHtml` flag.
   *
   * @param key - The localization token key used to find the corresponding localized string.
   * @param fields - (Optional) An array of objects with key-value pairs for placeholder substitution.
   * @param asHtml - (Optional) If true, returns a SafeHtml object for use with [innerHTML].
   * @returns Localized string with placeholder values replaced, either as string or SafeHtml.
   */
  getLocaleValue(key: localeValueKey, fields?: Record<string, any>[], asHtml: boolean = true): any {
    const localeValues: LocaleTokenSearch['localeTokenKeyToValue'] = this.localeValuesEntityService.localeTokenValues$.getValue();

    let value = localeValues[key] ?? key;

    if (!value) return key;


    if (fields?.length) {
      for (const field of fields) {
        for (const [k, v] of Object.entries(field)) {
          const regex = new RegExp(`{{\\s*${k}\\s*}}`, 'g');
          value = value.replace(regex, String(v));
        }
      }
    }


    if (asHtml) {
      value = `${ value } <i class="ltv" style="display: none;">${ key }</i>`;
    }


    return asHtml ? this.sanitizer.bypassSecurityTrustHtml(value) : value;
  }
}


export type localeValueKey =
  'patient.exam.collectors.page.job.limit.send.link.message' |
  'patient.exam.collectors.page.job.limit.sync.message' |
  'patient.exam.collectors.page.next.slide.toast' |
  'patient.exam.collectors.page.html.title' |
  'patient.exam.collectors.page.html.list.message1' |
  'patient.exam.collectors.page.html.list.message2' |
  'patient.exam.collectors.page.html.list.message3' |
  'patient.exam.collectors.page.html.box.title' |
  'patient.exam.collectors.page.html.box.message' |
  'patient.exam.collectors.page.html.btn.continue' |
  'patient.exam.collectors.page.html.btn.maybe.next.time' |
  'patient.referral.dialog.btn.close' |
  'patient.pdf.dialog.btn.close' |
  'icon.button.whatsapp.msg' |
  'patient.service.remove.exam.parse.title' |
  'patient.service.remove.exam.parse.btn.cancel.label' |
  'patient.service.remove.exam.parse.btn.confirm.label' |
  'patient.waiting.contact.page.title' |
  'patient.waiting.contact.page.message' |
  'patient.waiting.contact.page.title2' |
  'patient.home.page.status.wpe.type.exam.collector.title' |
  'patient.home.page.status.wpe.type.exam.collector.subtitle' |
  'patient.feedback.form.page.submit.toast' |
  'patient.exam.collectors.page.alert.no.pending.jobs' |
  'patient.exam.collectors.page.confirm.title' |
  'patient.exam.collectors.page.confirm.message' |
  'patient.exam.collectors.page.confirm.btn.confirm.label' |
  'patient.exam.collectors.page.confirm.sync.title' |
  'patient.exam.collectors.page.confirm.sync.message' |
  'patient.exam.collectors.page.confirm.sync.btn.confirm.label' |
  'patient.exam.collectors.page.confirm.user.data.error.title' |
  'patient.exam.collectors.page.confirm.user.data.error.message' |
  'patient.exam.collectors.page.confirm.user.data.error.btn.confirm.label' |
  'patient.exam.collectors.page.confirm.user.data.error.btn.cancel.label' |
  'patient.exam.collectors.page.html.link.sent' |
  'patient.exam.collectors.page.html.check.media' |
  'patient.exam.collectors.page.html.btn.error.data.user' |
  'patient.exam.collectors.page.html.connect.to' |
  'patient.exam.collectors.page.html.logo.list' |
  'patient.exam.collectors.page.html.how.works' |
  'patient.exam.collectors.page.html.how.works.list.title1' |
  'patient.exam.collectors.page.html.how.works.list.message1' |
  'patient.exam.collectors.page.html.how.works.list.title2' |
  'patient.exam.collectors.page.html.how.works.list.message2' |
  'patient.exam.collectors.page.html.how.works.list.title3' |
  'patient.exam.collectors.page.html.how.works.list.message3' |
  'patient.exam.collectors.page.html.access.grant.title' |
  'patient.exam.collectors.page.html.access.grant.message' |
  'patient.exam.collectors.page.html.btn.fake.resend.link' |
  'patient.exam.collectors.page.html.btn.sending' |
  'patient.exam.collectors.page.html.btn.resend.link' |
  'patient.exam.collectors.page.html.btn.already.connect' |
  'patient.exam.collectors.page.html.btn.error.data.user.last' |
  'patient.exam.collectors.page.html.btn.send.first' |
  'patient.exam.collectors.page.html.btn.sending.first' |
  'patient.pdf.dialog.btn.download' |
  'patient.form.finish.dialog.title' |
  'patient.form.finish.dialog.btn.continue' |
  'patient.form.finish.dialog.btn.cancel' |
  'patient.form.feedback.dialog.message' |
  'patient.form.feedback.dialog.title.about' |
  'patient.form.feedback.dialog.title.comment' |
  'patient.form.feedback.dialog.p.comment' |
  'patient.form.feedback.dialog.btn.send' |
  'patient.form.feedback.dialog.btn.close' |
  'patient.exam.collectors.logo.list.dialog.title' |
  'patient.exam.collectors.logo.list.dialog.btn' |
  'patient.login.signup.title.page.gender.type' |
  'patient.login.signup.title.gender.type' |
  'patient.login.signup.subtitle.page.gender.type' |
  'patient.login.signup.title.page.gender' |
  'patient.login.signup.title.gender' |
  'patient.login.signup.subtitle.page.gender' |
  'patient.login.signup.title.page.gender.other' |
  'patient.login.signup.title.gender.other' |
  'patient.login.signup.subtitle.page.gender.other' |
  'patient.login.signup.gender.type.form.opt.male.label' |
  'patient.login.signup.gender.type.form.opt.female.label' |
  'patient.login.signup.gender.type.form.opt.non.binary.label' |
  'patient.login.signup.gender.type.form.opt.other.label' |
  'patient.login.signup.gender.form.opt.male.label' |
  'patient.login.signup.gender.form.opt.female.label' |
  'patient.login.signup.gender.form.opt.non.binary.label' |
  'patient.login.signup.gender.form.opt.other.label' |
  'patient.login.signup.gender.form.opt.unspecified.label' |
  'patient.login.signup.gender.form.label' |
  'patient.login.signup.gender.form.message' |
  'patient.login.signup.gender.type.dialog.message' |
  'patient.login.data.confirmation.next.questions.title' |
  'patient.login.data.confirmation.next.questions.title.page' |
  'patient.login.data.confirmation.next.questions.subtitle' |
  'patient.login.data.confirmation.gender.type.title' |
  'patient.login.data.confirmation.gender.type.title.page' |
  'patient.login.data.confirmation.gender.type.subtitle' |
  'patient.login.data.confirmation.gender.type.form.opt.male.label' |
  'patient.login.data.confirmation.gender.type.form.opt.female.label' |
  'patient.login.data.confirmation.gender.type.form.opt.non.binary.label' |
  'patient.login.data.confirmation.gender.type.form.opt.other.label' |
  'patient.login.data.confirmation.gender.type.dialog.message' |
  'patient.login.data.confirmation.age.btn' |
  'patient.login.data.confirmation.age.title' |
  'patient.login.data.confirmation.age.title.page' |
  'patient.login.data.confirmation.age.form.label' |
  'patient.login.data.confirmation.age.form.placeholder' |
  'patient.login.data.confirmation.age.logout.title' |
  'patient.login.data.confirmation.age.logout.message' |
  'patient.login.data.confirmation.age.logout.btn.confirm.label' |
  'patient.login.data.confirmation.age.logout.btn.cancel.label' |
  'patient.login.data.confirmation.biological.sex.title' |
  'patient.login.data.confirmation.biological.sex.title.page' |
  'patient.login.data.confirmation.biological.sex.subtitle' |
  'patient.login.data.confirmation.biological.sex.form.opt.male.label' |
  'patient.login.data.confirmation.biological.sex.form.opt.female.label' |
  'patient.login.data.confirmation.gender.title' |
  'patient.login.data.confirmation.gender.title.page' |
  'patient.login.data.confirmation.gender.subtitle' |
  'patient.login.data.confirmation.gender.form.opt.male.label' |
  'patient.login.data.confirmation.gender.form.opt.female.label' |
  'patient.login.data.confirmation.gender.form.opt.non.binary.label' |
  'patient.login.data.confirmation.gender.form.opt.other.label' |
  'patient.login.data.confirmation.gender.form.opt.unspecified.label' |
  'patient.login.data.confirmation.gender.other.title' |
  'patient.login.data.confirmation.gender.other.title.page' |
  'patient.login.data.confirmation.gender.other.subtitle' |
  'patient.login.data.confirmation.gender.other.form.label' |
  'patient.login.data.confirmation.gender.other.form.validation.max.length' |
  'patient.form.ending.loading.progress.1.title' |
  'patient.form.ending.loading.progress.1.detail' |
  'patient.form.ending.loading.progress.2.title' |
  'patient.form.ending.loading.progress.2.detail' |
  'patient.form.ending.loading.progress.3.title' |
  'patient.form.ending.loading.progress.3.detail' |
  'patient.form.ending.loading.progress.4.title' |
  'patient.form.ending.loading.progress.4.detail' |
  'patient.summary.referral.dialog.partial.name.download' |
  'patient.summary.referral.dialog.file.not.found' |
  'patient.summary.referral.dialog.error.url' |
  'patient.summary.referral.dialog.error.document' |
  'patient.summary.referral.dialog.share.not.compatible' |
  'patient.summary.referral.dialog.share.error' |
  'patient.summary.referral.dialog.title' |
  'patient.summary.referral.dialog.btn.text' |
  'patient.login.code.media.whatsapp' |
  'patient.login.code.media.sms' |
  'patient.onboarding.files.page.title' |
  'patient.onboarding.files.page.onboarding.1.title' |
  'patient.onboarding.files.page.onboarding.1.text' |
  'patient.onboarding.files.page.onboarding.2.title' |
  'patient.onboarding.files.page.onboarding.2.text' |
  'patient.onboarding.files.page.onboarding.3.title' |
  'patient.onboarding.files.page.onboarding.3.text' |
  'patient.onboarding.files.page.btn.next' |
  'patient.onboarding.files.page.btn.picture' |
  'patient.onboarding.files.page.btn.file' |
  'patient.select.method.upload.file.dialog.title' |
  'patient.select.method.upload.file.dialog.btn.camera' |
  'patient.select.method.upload.file.dialog.btn.files' |
  'patient.form.continue.title' |
  'patient.form.continue.message' |
  'patient.form.continue.btn.confirm' |
  'patient.form.submit.form.slow' |
  'patient.form.submit.form.critical.error' |
  'patient.form.set.input.number.placeholder' |
  'patient.form.set.input.number.validation.integer.msg' |
  'patient.form.set.input.number.validation.min.msg' |
  'patient.form.set.input.number.validation.max.msg' |
  'patient.form.set.input.text.placeholder' |
  'patient.form.set.input.text.validation.min.msg' |
  'patient.form.set.input.text.validation.max.msg' |
  'patient.form.dialog.reason.title' |
  'patient.form.dialog.reason.label' |
  'patient.form.btn.back' |
  'patient.form.btn.why.ask' |
  'patient.form.btn.continue' |
  'patient.form.btn.finalize' |
  'patient.error.dialog.btn.close' |
  'patient.error.dialog.btn.continue' |
  'patient.error.dialog.whatsapp.msg.no.sub' |
  'patient.error.dialog.whatsapp.msg.no.org' |
  'patient.error.dialog.whatsapp.msg' |
  'patient.error.dialog.no.org.title' |
  'patient.error.dialog.no.org.text' |
  'patient.error.dialog.login.title' |
  'patient.error.dialog.login.text' |
  'patient.error.dialog.no.sub.title' |
  'patient.error.dialog.no.sub.text' |
  'patient.error.dialog.biologicalsex.title' |
  'patient.error.dialog.biologicalsex.text' |
  'patient.error.dialog.age.title' |
  'patient.error.dialog.age.text' |
  'patient.error.dialog.document.title' |
  'patient.error.dialog.document.text' |
  'patient.unmet.recommendation.exams.title' |
  'patient.unmet.recommendation.exams.text.waiting.analysis' |
  'patient.unmet.recommendation.exams.text.waiting.analysis.retry' |
  'patient.waiting.performed.exams.results.page.title' |
  'patient.waiting.performed.exams.results.page.subtitle' |
  'patient.waiting.performed.exams.results.page.alert.text' |
  'patient.waiting.performed.exams.results.page.file.sent.title' |
  'patient.waiting.performed.exams.results.page.file.sent.empty.state' |
  'patient.waiting.performed.exams.results.page.file.sent.hint.analysis' |
  'patient.waiting.performed.exams.results.page.file.sent.analyzed.title' |
  'patient.waiting.performed.exams.results.page.file.sent.analyzed.empty.state' |
  'patient.waiting.performed.exams.results.page.file.sent.analyzed.hint.bad.content' |
  'patient.waiting.performed.exams.results.page.file.sent.analyzed.hint.success' |
  'patient.waiting.performed.exams.results.page.button.send.exams' |
  'patient.login.intro.change.locale' |
  'menu.layout.item.support' |
  'menu.layout.item.locale' |
  'menu.layout.item.security' |
  'menu.layout.item.logout' |
  'patient.locale.page.title' |
  'patient.feedback.service.bottom.sheet.feedback.active.title' |
  'patient.feedback.service.bottom.sheet.feedback.active.message' |
  'patient.feedback.service.bottom.sheet.feedback.active.button.confirm.label' |
  'patient.feedback.service.bottom.sheet.feedback.active.button.cancel.label' |
  'patient.feedback.service.bottom.sheet.feedback.survey.title' |
  'patient.feedback.service.bottom.sheet.feedback.survey.message' |
  'patient.feedback.service.bottom.sheet.feedback.survey.button.confirm.label' |
  'patient.feedback.service.bottom.sheet.feedback.survey.button.cancel.label' |
  'patient.feedback.service.config.feedback.footer.title' |
  'patient.feedback.service.config.feedback.footer.field.prop.label' |
  'patient.feedback.service.config.feedback.footer.field.prop.placeholder' |
  'patient.feedback.service.config.feedback.active.title' |
  'patient.feedback.service.config.feedback.active.field.customer.satisfaction.option.very.satisfied' |
  'patient.feedback.service.config.feedback.active.field.customer.satisfaction.option.satisfied' |
  'patient.feedback.service.config.feedback.active.field.customer.satisfaction.option.neutral' |
  'patient.feedback.service.config.feedback.active.field.customer.satisfaction.option.dissatisfied' |
  'patient.feedback.service.config.feedback.active.field.customer.satisfaction.option.very.dissatisfied' |
  'patient.feedback.service.config.feedback.active.2.title' |
  'patient.feedback.service.config.feedback.active.2.subtitle' |
  'patient.feedback.service.config.feedback.active.2.field.comment.label' |
  'patient.feedback.service.config.feedback.active.2.field.comment.placeholder' |
  'patient.feedback.service.config.feedback.survey.title' |
  'patient.feedback.service.config.feedback.survey.subtitle' |
  'patient.feedback.service.config.feedback.survey.field.email.label' |
  'patient.feedback.service.config.feedback.survey.field.email.placeholder' |
  'patient.feedback.service.config.feedback.survey.field.phone.label' |
  'patient.feedback.service.config.feedback.survey.field.phone.placeholder' |
  'patient.feedback.main.title' |
  'patient.feedback.main.button.yes' |
  'patient.feedback.main.button.no' |
  'patient.feedback.response.title' |
  'patient.feedback.response.subtitle' |
  'patient.feedback.response.button.link' |
  'patient.woman.area.where.to.get.help.page.title.page' |
  'patient.woman.area.where.to.get.help.page.women.defense.police.station.ddm.title' |
  'patient.woman.area.where.to.get.help.page.women.defense.police.station.ddm.subtitle' |
  'patient.woman.area.where.to.get.help.page.public.defenders.office.title' |
  'patient.woman.area.where.to.get.help.page.public.defenders.office.subtitle' |
  'patient.woman.area.where.to.get.help.page.brazilian.woman.house.title' |
  'patient.woman.area.where.to.get.help.page.brazilian.woman.house.subtitle' |
  'patient.woman.area.where.to.get.help.page.woman.service.center.180.title' |
  'patient.woman.area.where.to.get.help.page.woman.service.center.180.subtitle' |
  'patient.woman.area.where.to.get.help.page.woman.service.center.180.label' |
  'patient.woman.area.where.to.get.help.page.emergency.190.title' |
  'patient.woman.area.where.to.get.help.page.emergency.190.subtitle' |
  'patient.woman.area.where.to.get.help.page.emergency.190.label' |
  'patient.woman.area.where.to.get.help.page.social.assistance.cras.title' |
  'patient.woman.area.where.to.get.help.page.social.assistance.cras.subtitle' |
  'patient.woman.area.where.to.get.help.page.social.assistance.cras.label' |
  'patient.woman.area.where.to.get.help.page.content.header' |
  'patient.woman.area.where.to.get.help.page.panel.title' |
  'patient.woman.area.violence.what.now.page.title.page' |
  'patient.woman.area.violence.what.now.page.you.are.not.alone.title' |
  'patient.woman.area.violence.what.now.page.you.are.not.alone.subtitle' |
  'patient.woman.area.violence.what.now.page.it.is.never.your.fault.title' |
  'patient.woman.area.violence.what.now.page.it.is.never.your.fault.subtitle' |
  'patient.woman.area.violence.what.now.page.respect.your.feelings.title' |
  'patient.woman.area.violence.what.now.page.respect.your.feelings.subtitle' |
  'patient.woman.area.violence.what.now.page.recovery.is.possible.title' |
  'patient.woman.area.violence.what.now.page.recovery.is.possible.subtitle' |
  'patient.woman.area.violence.what.now.page.seek.support.network.title' |
  'patient.woman.area.violence.what.now.page.seek.support.network.subtitle' |
  'patient.woman.area.violence.what.now.page.take.care.of.mental.health.title' |
  'patient.woman.area.violence.what.now.page.take.care.of.mental.health.subtitle' |
  'patient.woman.area.violence.what.now.page.make.safety.plan.title' |
  'patient.woman.area.violence.what.now.page.make.safety.plan.subtitle' |
  'patient.woman.area.violence.what.now.page.make.safety.plan.label' |
  'patient.woman.area.violence.what.now.page.legal.support.title' |
  'patient.woman.area.violence.what.now.page.legal.support.subtitle' |
  'patient.woman.area.violence.what.now.page.content.header' |
  'patient.woman.area.violence.what.now.page.support.center.woman.180' |
  'patient.woman.area.violence.what.now.page.support.center.woman.180.btn' |
  'patient.woman.area.violence.what.now.page.where.get.help' |
  'patient.woman.area.safety.plan.page.title.page' |
  'patient.woman.area.safety.plan.page.emergency.help.title' |
  'patient.woman.area.safety.plan.page.emergency.help.subtitle' |
  'patient.woman.area.safety.plan.page.home.risk.reduction.title' |
  'patient.woman.area.safety.plan.page.home.risk.reduction.subtitle' |
  'patient.woman.area.safety.plan.page.escape.planning.title' |
  'patient.woman.area.safety.plan.page.escape.planning.subtitle' |
  'patient.woman.area.safety.plan.page.essential.items.title' |
  'patient.woman.area.safety.plan.page.essential.items.subtitle' |
  'patient.woman.area.safety.plan.page.financial.issues.title' |
  'patient.woman.area.safety.plan.page.financial.issues.subtitle' |
  'patient.woman.area.safety.plan.page.content.header' |
  'patient.woman.area.safety.plan.page.where.get.help' |
  'patient.woman.area.page.title.page' |
  'patient.woman.area.page.card.about.violence.title' |
  'patient.woman.area.page.card.about.violence.subtitle' |
  'patient.woman.area.page.card.violence.what.now.title' |
  'patient.woman.area.page.card.violence.what.now.subtitle' |
  'patient.woman.area.page.card.where.get.help.title' |
  'patient.woman.area.page.card.where.get.help.subtitle' |
  'patient.woman.area.page.card.safety.plan.title' |
  'patient.woman.area.page.card.safety.plan.subtitle' |
  'patient.woman.area.page.content.header' |
  'patient.woman.area.about.violence.page.title.page' |
  'patient.woman.area.about.violence.page.physical.title' |
  'patient.woman.area.about.violence.page.physical.subtitle' |
  'patient.woman.area.about.violence.page.psychological.title' |
  'patient.woman.area.about.violence.page.psychological.subtitle' |
  'patient.woman.area.about.violence.page.sexual.title' |
  'patient.woman.area.about.violence.page.sexual.subtitle' |
  'patient.woman.area.about.violence.page.property.title' |
  'patient.woman.area.about.violence.page.property.subtitle' |
  'patient.woman.area.about.violence.page.moral.title' |
  'patient.woman.area.about.violence.page.moral.subtitle' |
  'patient.woman.area.about.violence.page.is.violence' |
  'button.call.190' |
  'patient.waiting.performed.exams.download.share.reason.page.title.page' |
  'patient.waiting.performed.exams.download.share.page.title.page' |
  'patient.waiting.performed.exams.download.share.page.title' |
  'patient.waiting.performed.exams.download.share.page.subtitle' |
  'patient.waiting.performed.exams.download.share.page.whatsapp.msg' |
  'patient.waiting.performed.exams.download.share.page.download.confirm.title' |
  'patient.waiting.performed.exams.download.share.page.download.confirm.msg' |
  'patient.waiting.performed.exams.download.share.page.download.confirm.btn.confirm' |
  'patient.waiting.performed.exams.download.share.page.validate.confirm.title' |
  'patient.waiting.performed.exams.download.share.page.validate.confirm.msg' |
  'patient.waiting.performed.exams.download.share.page.validate.confirm.btn.confirm' |
  'patient.waiting.performed.exams.download.share.page.share.file.name' |
  'patient.waiting.performed.exams.download.share.page.share.title' |
  'patient.waiting.performed.exams.download.share.page.share.error.msg' |
  'patient.waiting.performed.exams.download.share.page.share.error.notallowed.msg' |
  'patient.waiting.performed.exams.download.share.page.showexams.msg' |
  'patient.waiting.performed.exams.download.share.page.showexams.btn.msg' |
  'patient.waiting.performed.exams.download.share.page.help.text' |
  'patient.waiting.performed.exams.download.share.page.help.text.pt2' |
  'patient.waiting.performed.exams.download.share.page.free.exams.text' |
  'patient.waiting.performed.exams.download.share.page.free.exams.text.pt2' |
  'patient.waiting.performed.exams.download.share.page.file.list.title' |
  'patient.waiting.performed.exams.download.share.page.exam.request.status.complete' |
  'patient.waiting.performed.exams.download.share.page.exam.request.status.pending' |
  'patient.waiting.performed.exams.download.share.page.exam.request.status.partial' |
  'patient.waiting.performed.exams.download.share.page.exam.preparation' |
  'patient.waiting.performed.exams.download.share.page.exam.reason' |
  'patient.waiting.performed.exams.download.share.instructions.page.title.page' |
  'patient.file.previous.list.title.page' |
  'patient.file.previous.list.success.msg' |
  'patient.file.previous.list.success.btn' |
  'patient.file.previous.list.confirm.title' |
  'patient.file.previous.list.confirm.btn.confirm' |
  'patient.file.previous.list.confirm.btn.cancel' |
  'patient.file.previous.list.title' |
  'patient.file.previous.list.btn.crop.image' |
  'patient.file.previous.list.btn.finish' |
  'patient.camera.crop.title.page' |
  'button.confirm' |
  'button.cancel' |
  'patient.camera.capture.title.page' |
  'patient.camera.capture.camera.error.not.allowed.msg' |
  'patient.camera.capture.camera.error.not.found.msg' |
  'patient.camera.capture.title' |
  'patient.camera.capture.frame.overlay' |
  'patient.home.page.title.page' |
  'patient.home.page.status.wpe.type.partial.summary.title' |
  'patient.home.page.status.wpe.type.partial.summary.subtitle' |
  'patient.home.page.status.wpe.type.exam.request.title' |
  'patient.home.page.status.wpe.type.exam.request.subtitle' |
  'patient.home.page.status.wpe.type.exam.results.title' |
  'patient.home.page.status.wpe.type.exam.results.subtitle' |
  'patient.home.page.status.wpe.type.woman.title' |
  'patient.home.page.status.wpe.type.woman.subtitle' |
  'patient.home.page.status.finished.type.summary.title' |
  'patient.home.page.status.finished.type.summary.subtitle' |
  'patient.home.page.status.finished.type.results.title' |
  'patient.home.page.status.finished.type.results.subtitle' |
  'patient.home.page.status.finished.type.woman.title' |
  'patient.home.page.status.finished.type.woman.subtitle' |
  'patient.home.page.bad.content.msg' |
  'patient.home.page.bad.content.btn' |
  'patient.home.page.title' |
  'patient.onboarding.page.title.page' |
  'patient.onboarding.page.title' |
  'patient.onboarding.page.item.cognition' |
  'patient.onboarding.page.item.schedule' |
  'patient.onboarding.page.item.home.health' |
  'patient.onboarding.page.item.lock' |
  'patient.onboarding.page.item.favorite' |
  'patient.onboarding.page.button.start' |
  'patient.legal.terms.details.page.title.page' |
  'patient.legal.terms.page.title.page' |
  'patient.legal.terms.page.title' |
  'patient.legal.terms.page.subtitle' |
  'patient.legal.terms.page.button.show' |
  'patient.legal.terms.page.button.accept' |
  'patient.form.steps.title.page.age' |
  'patient.form.steps.title.age' |
  'patient.form.steps.form.age.label' |
  'patient.form.steps.form.age.placeholder' |
  'patient.form.steps.title.page.biologicalsex' |
  'patient.form.steps.title.biologicalsex' |
  'patient.form.steps.subtitle.biologicalsex' |
  'patient.form.steps.form.biologicalsex.male' |
  'patient.form.steps.form.biologicalsex.female' |
  'patient.form.steps.confirm.title' |
  'patient.form.steps.confirm.message' |
  'patient.form.steps.confirm.button.confirm' |
  'patient.form.steps.question.why.title' |
  'patient.login.code.title.page' |
  'patient.login.code.form.phonecode.label' |
  'patient.login.code.form.phonecode.placeholder' |
  'patient.login.code.title' |
  'patient.login.code.submit.error.code.500' |
  'patient.login.code.submit.error.code.50008' |
  'patient.login.code.submit.error.code.else' |
  'patient.login.code.challenge.error.closedunexpectedly' |
  'patient.login.code.challenge.error.fail' |
  'patient.login.code.confirm.title' |
  'patient.login.code.confirm.message' |
  'patient.login.code.confirm.button.confirm' |
  'patient.login.code.confirm.button.cancel' |
  'patient.login.code.challenge.choosechannel.error' |
  'patient.login.code.challenge.choosechannel.error.resend' |
  'patient.login.code.code.sent' |
  'patient.login.code.dialog.help.confirm.title' |
  'patient.login.code.dialog.help.confirm.message' |
  'patient.login.code.dialog.help.confirm.button.confirm' |
  'patient.login.code.dialog.help.confirm.button.cancel' |
  'patient.login.code.dialog.help.msg' |
  'patient.login.code.button.resend' |
  'patient.login.signup.form.name.message' |
  'patient.login.signup.form.name.label' |
  'patient.login.signup.form.birthdate.label' |
  'patient.login.signup.form.biologicalsex.label.male' |
  'patient.login.signup.form.biologicalsex.label.female' |
  'patient.login.signup.confirm.button.confirm' |
  'patient.login.signup.confirm.message' |
  'patient.login.signup.confirm.title' |
  'patient.login.signup.question.why.title' |
  'patient.login.signup.subtitle.biologicalsex' |
  'patient.login.signup.title.biologicalsex' |
  'patient.login.signup.title.page.biologicalsex' |
  'patient.login.signup.subtitle.birthday' |
  'patient.login.signup.title.birthday' |
  'patient.login.signup.title.page.birthday' |
  'patient.login.signup.subtitle.name' |
  'patient.login.signup.title.name' |
  'patient.login.signup.title.page.name' |
  'patient.login.phone.confirm.button.cancel' |
  'patient.login.phone.confirm.button.confirm' |
  'patient.login.phone.confirm.message' |
  'patient.login.phone.confirm.title' |
  'patient.login.phone.form.document.placeholder' |
  'patient.login.phone.form.document.label' |
  'patient.login.phone.subtitle' |
  'patient.login.phone.title' |
  'patient.login.phone.title.page' |
  'patient.login.document.whatsapp.msg' |
  'patient.login.document.confirm.button.cancel' |
  'patient.login.document.confirm.button.confirm' |
  'patient.login.document.confirm.message' |
  'patient.login.document.confirm.title' |
  'patient.login.document.form.document.placeholder' |
  'patient.login.document.form.document.label' |
  'patient.login.document.subtitle' |
  'patient.login.document.title' |
  'patient.login.document.title.page' |
  'button.history.back' |
  'button.next' |
  'patient.login.intro.start.button' |
  'patient.login.intro.title.page' |
  'patient.login.intro.step.text' |
  'patient.login.intro.step.label' |
  'patient.login.intro.welcome.subtitle' |
  'patient.login.intro.welcome'
  | 'patient.doctor.page.ts.doctors.index0.name'
  | 'patient.doctor.page.ts.doctors.index0.description'
  | 'patient.doctor.page.ts.doctors.index1.name'
  | 'patient.doctor.page.ts.doctors.index1.description'
  | 'patient.doctor.page.ts.doctors.index2.name'
  | 'patient.doctor.page.ts.doctors.index2.description'
  | 'patient.doctor.page.html.h1.title'
  | 'patient.about.checkup.page.html.image.alt'
  | 'patient.about.checkup.page.html.h1.title'
  | 'patient.about.checkup.page.html.section1.subtitle'
  | 'patient.about.checkup.page.html.section1.description.one'
  | 'patient.about.checkup.page.html.section1.description.two'
  | 'patient.about.checkup.page.html.section1.description.three'
  | 'patient.about.checkup.page.html.section2.subtitle'
  | 'patient.about.checkup.page.html.section2.description.one'
  | 'patient.about.checkup.page.html.section2.description.two'
  | 'patient.about.checkup.page.html.section2.description.three'
  | 'patient.about.checkup.page.html.section3.subtitle'
  | 'patient.about.checkup.page.html.section3.description.one'
  | 'patient.about.checkup.page.html.section3.description.two'
  | 'patient.about.checkup.page.html.section3.description.three'
  | 'patient.about.checkup.page.html.alert.title'
  | 'patient.about.checkup.page.html.alert.description'
  | 'patient.home.v2.page.ts.set.data.shared.section.footer.title'
  | 'patient.home.v2.page.ts.set.data.shared.nav.card.footer.title'
  | 'patient.home.v2.page.ts.set.data.shared.quick.button.footer.list.index0.label'
  | 'patient.home.v2.page.ts.set.data.shared.quick.button.footer.list.index1.label'
  | 'patient.home.v2.page.ts.set.data.shared.quick.button.footer.list.index2.label'
  | 'patient.home.v2.page.ts.set.data.shared.card.footer.title'
  | 'patient.home.v2.page.ts.set.data.shared.card.footer.subtitle'
  | 'patient.home.v2.page.ts.set.data.shared.card.footer.button.title'
  | 'patient.home.v2.page.ts.set.data.finished.section.header.title'
  | 'patient.home.v2.page.ts.set.data.finished.section.header.subtitle'
  | 'patient.home.v2.page.ts.set.data.finished.card.header.title'
  | 'patient.home.v2.page.ts.set.data.finished.card.header.subtitle'
  | 'patient.home.v2.page.ts.set.data.finished.card.header.button.title'
  | 'patient.home.v2.page.ts.set.data.finished.nav.card.title'
  | 'patient.home.v2.page.ts.set.data.waiting.performed.exams.section.header.title'
  | 'patient.home.v2.page.ts.set.data.waiting.performed.exams.card.header.title'
  | 'patient.home.v2.page.ts.set.data.waiting.performed.exams.card.header.subtitle'
  | 'patient.home.v2.page.ts.set.data.waiting.performed.exams.card.header.button.title'
  | 'patient.home.v2.page.ts.set.data.waiting.performed.exams.nav.card.title'
  | 'patient.home.v2.page.ts.set.data.waiting.performed.exams.nav.card.badge'
  | 'patient.home.v2.page.ts.open.whatsapp.msg'
  | 'patient.exams.page.ts.title'
  | 'patient.exams.page.ts.subtitle'
  | 'patient.exams.page.ts.title.list'
  | 'patient.exams.page.ts.alert.title'
  | 'patient.exams.page.ts.alert.message'
  | 'patient.exams.page.ts.alert.button.text'
  | 'patient.exams.page.ts.action.buttons.index0.title'
  | 'patient.exams.page.ts.action.buttons.index1.title'
  | 'patient.exams.page.ts.section.highlight.title'
  | 'patient.exams.page.ts.section.highlight.message'
  | 'patient.exams.page.ts.section.highlight.text.link'
  | 'patient.exams.page.ts.status.pending'
  | 'patient.exams.page.ts.status.partial'
  | 'patient.exams.page.ts.status.complete'
  | 'patient.exams.page.ts.section.footer.title'
  | 'patient.exams.page.ts.nav.card.footer.title'
  | 'patient.exams.page.ts.quick.button.footer.list.index0.label'
  | 'patient.exams.page.ts.quick.button.footer.list.index1.label'
  | 'patient.exams.page.ts.quick.button.footer.list.index2.label'
  | 'patient.exams.page.ts.card.footer.title'
  | 'patient.exams.page.ts.card.footer.subtitle'
  | 'patient.exams.page.ts.card.footer.button.title'
  | 'patient.exams.page.html.h2.title'
  | 'patient.exam.requests.dialog.html.h3.title'
  | 'patient.exam.requests.dialog.html.btn.instructions'
  | 'patient.exam.requests.dialog.html.btn.close'
  | 'patient.blood.pressure.page.ts.list.index0.title'
  | 'patient.blood.pressure.page.ts.list.index0.subtitle'
  | 'patient.blood.pressure.page.ts.list.index1.title'
  | 'patient.blood.pressure.page.ts.list.index1.subtitle'
  | 'patient.blood.pressure.page.ts.list.index2.title'
  | 'patient.blood.pressure.page.ts.list.index2.subtitle'
  | 'patient.blood.pressure.page.html.h2.title'
  | 'patient.blood.pressure.page.html.btn.send.results'
  | 'patient.exam.requests.instructions.page.html.btn.blood.pressure'
  | 'patient.exam.previous.page.ts.list.lab.index0.title'
  | 'patient.exam.previous.page.ts.list.lab.index0.subtitle'
  | 'patient.exam.previous.page.ts.list.send.exams.index0.title'
  | 'patient.exam.previous.page.ts.list.send.exams.index0.subtitle'
  | 'patient.exam.previous.page.ts.list.send.exams.index1.title'
  | 'patient.exam.previous.page.ts.list.send.exams.index1.subtitle'
  | 'patient.exam.previous.page.html.h2.title'
  | 'patient.exam.previous.page.html.h3.title.auto'
  | 'patient.exam.previous.page.html.btn.connect'
  | 'patient.exam.previous.page.html.h3.title.manual'
  | 'patient.exam.previous.page.html.btn.send'
  | 'patient.exam.requests.page.ts.get.first.name.default'
  | 'patient.exam.requests.page.html.h2.title'
  | 'patient.exam.collectors.page.ts.title.page'
  | 'patient.exam.collectors.page.ts.list.how.works.index0.title'
  | 'patient.exam.collectors.page.ts.list.how.works.index1.title'
  | 'patient.exam.collectors.page.ts.list.how.works.index2.title'
  | 'patient.exam.collectors.page.ts.list.how.works.index3.title'
  | 'patient.exam.collectors.page.ts.user.data.error.title'
  | 'patient.exam.collectors.page.ts.user.data.error.message'
  | 'patient.exam.collectors.page.ts.user.data.error.btn.confirm'
  | 'patient.exam.collectors.page.ts.user.data.error.btn.cancel'
  | 'patient.exam.collectors.page.ts.get.title.dasa'
  | 'patient.exam.collectors.page.ts.get.title.fleury'
  | 'patient.exam.collectors.page.ts.get.title.pardini'
  | 'patient.exam.collectors.page.ts.get.title.default'
  | 'patient.exam.collectors.page.ts.open.about.beta.title'
  | 'patient.exam.collectors.page.ts.open.about.beta.message'
  | 'patient.exam.collectors.page.ts.open.about.beta.btn.confirm'
  | 'patient.exam.collectors.page.html.onboarding.btn.beta'
  | 'patient.exam.collectors.page.html.onboarding.h2.title'
  | 'patient.exam.collectors.page.html.onboarding.p.subtitle'
  | 'patient.exam.collectors.page.html.onboarding.btn.connect'
  | 'patient.exam.collectors.page.html.available.link.not.sent.h2.title'
  | 'patient.exam.collectors.page.html.available.link.not.sent.h3.title'
  | 'patient.exam.collectors.page.html.available.link.not.sent.p.subtitle'
  | 'patient.exam.collectors.page.html.available.link.not.sent.card.email'
  | 'patient.exam.collectors.page.html.available.link.not.sent.card.sms'
  | 'patient.exam.collectors.page.html.available.link.not.sent.btn.error'
  | 'patient.exam.collectors.page.html.available.link.not.sent.btn.continue'
  | 'patient.exam.collectors.page.html.connect.h2.title'
  | 'patient.exam.collectors.page.html.connect.h3.title'
  | 'patient.exam.collectors.page.html.connect.btn.continue'
  | 'patient.exam.collectors.page.html.available.link.sent.h2.title'
  | 'patient.exam.collectors.page.html.available.link.sent.p.subtitle'
  | 'patient.exam.collectors.page.html.available.link.sent.btn.resend'
  | 'patient.exam.collectors.page.html.available.link.sent.btn.resend.timer'
  | 'patient.exam.collectors.page.html.available.link.sent.btn.connected'
  | 'patient.exam.collectors.page.html.too.many.jobs.h2.title'
  | 'patient.exam.collectors.page.html.too.many.jobs.p.subtitle'
  | 'patient.exam.collectors.page.html.too.many.jobs.btn.continue'
  | 'patient.exam.collectors.page.html.active.h2.title'
  | 'patient.exam.collectors.page.html.active.h2.title.pt2'
  | 'patient.exam.collectors.page.html.active.btn.continue'
  | 'patient.exam.requests.dialog.html.badge.received'
  | 'patient.exam.requests.dialog.html.badge.not.received';
